import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useProductBulkImport } from '../components/useProductBulkImport';
import { useCatalogImportJobInbox } from '../hooks/useCatalogImportJobInbox';

type ImportState = ReturnType<typeof useProductBulkImport>;

type ProductBulkImportContextValue = {
  importState: ImportState;
  importOpen: boolean;
  setImportOpen: (open: boolean) => void;
  openImportPopover: () => void;
  registerOnImported: (fn: () => void) => void;
  /** Throttled while import runs — prefer silent list refresh (no loading overlay). */
  registerOnImportProgress: (fn: () => void) => void;
  /** Extra inbox badge slots while import progress / result / error visible. */
  inboxImportItemCount: number;
  /** Best-effort job id for inbox detail drill-down. */
  trackedImportJobId: string | null;
  trackedImportFileName: string | null;
};

const Ctx = createContext<ProductBulkImportContextValue | null>(null);

/** Min interval / batch size before refreshing catalog list during import. */
const LIVE_REFRESH_MIN_MS = 1200;
const LIVE_REFRESH_MIN_CREATED_DELTA = 3;

export function ProductBulkImportProvider({ children }: { children: ReactNode }) {
  const [importOpen, setImportOpen] = useState(false);
  const location = useLocation();
  const onImportedRef = useRef<() => void>(() => {});
  const onImportProgressRef = useRef<() => void>(() => {});
  const lastLiveRefreshRef = useRef({ at: 0, created: 0 });

  const registerOnImported = useCallback((fn: () => void) => {
    onImportedRef.current = fn;
  }, []);

  const registerOnImportProgress = useCallback((fn: () => void) => {
    onImportProgressRef.current = fn;
  }, []);

  /** Import confirm table uses fixed-height scroll — close on route change to avoid layout measure across pages. */
  useEffect(() => {
    if (!importOpen) return;
    setImportOpen(false);
  }, [location.pathname]);

  const importState = useProductBulkImport({
    onImported: () => onImportedRef.current(),
    onImportOpenChange: setImportOpen,
  });

  const inboxJob = useCatalogImportJobInbox();
  const inboxCompletedRef = useRef<string | null>(null);

  const { importProgress, result, err, jobInFlight } = importState;

  const effectiveProgress = importProgress ?? inboxJob.importProgress;
  const effectiveResult =
    result ??
    (inboxJob.showResult && inboxJob.job
      ? {
          created: inboxJob.job.createdCount,
          failed: inboxJob.job.failedCount,
          results: inboxJob.job.sampleErrors,
          provisioning: inboxJob.job.provisioning ?? undefined,
        }
      : null);
  const effectiveErr =
    err ?? (inboxJob.err && (effectiveProgress || effectiveResult) ? inboxJob.err : null);
  const effectiveInFlight = jobInFlight || inboxJob.inFlight;

  const liveCreatedCount = useMemo(() => inboxJob.job?.createdCount ?? 0, [inboxJob.job?.createdCount]);

  useEffect(() => {
    if (!effectiveInFlight) {
      lastLiveRefreshRef.current = { at: 0, created: 0 };
      return;
    }
    const created = liveCreatedCount;
    if (created <= 0) return;

    const prev = lastLiveRefreshRef.current;
    const now = Date.now();
    const delta = created - prev.created;
    if (delta <= 0) return;
    if (
      prev.created > 0 &&
      now - prev.at < LIVE_REFRESH_MIN_MS &&
      delta < LIVE_REFRESH_MIN_CREATED_DELTA
    ) {
      return;
    }

    lastLiveRefreshRef.current = { at: now, created };
    onImportProgressRef.current();
  }, [effectiveInFlight, liveCreatedCount]);

  useEffect(() => {
    const job = inboxJob.job;
    if (!job || job.status !== 'COMPLETED') return;
    if (inboxCompletedRef.current === job.jobId) return;
    inboxCompletedRef.current = job.jobId;
    if (job.createdCount > 0) onImportedRef.current();
  }, [inboxJob.job]);

  const inboxImportItemCount = useMemo(() => {
    let n = 0;
    if (effectiveProgress && effectiveInFlight) n += 1;
    if (effectiveResult) n += 1;
    if (effectiveErr && (effectiveProgress || effectiveResult || effectiveInFlight)) n += 1;
    return n;
  }, [effectiveProgress, effectiveResult, effectiveErr, effectiveInFlight]);

  const mergedImportState = useMemo(
    () => ({
      ...importState,
      importProgress: effectiveProgress,
      result: effectiveResult,
      err: effectiveErr ?? importState.err,
      jobInFlight: effectiveInFlight,
      jobStatus: inboxJob.job?.status ?? null,
      jobCreatedCount: inboxJob.job?.createdCount ?? 0,
      closeImportPopover: () => {
        importState.closeImportPopover();
        if (inboxJob.showResult) inboxJob.dismissResult();
      },
    }),
    [importState, effectiveProgress, effectiveResult, effectiveErr, effectiveInFlight, inboxJob],
  );

  const trackedImportJobId =
    inboxJob.job?.jobId ?? mergedImportState.activeJobId ?? null;
  const trackedImportFileName = inboxJob.job?.fileName ?? null;

  const value = useMemo(
    () => ({
      importState: mergedImportState,
      importOpen,
      setImportOpen,
      openImportPopover: () => setImportOpen(true),
      registerOnImported,
      registerOnImportProgress,
      inboxImportItemCount,
      trackedImportJobId,
      trackedImportFileName,
    }),
    [
      mergedImportState,
      importOpen,
      registerOnImported,
      registerOnImportProgress,
      inboxImportItemCount,
      trackedImportJobId,
      trackedImportFileName,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProductBulkImportContextOptional(): ProductBulkImportContextValue | null {
  return useContext(Ctx);
}

export function useProductBulkImportContext(): ProductBulkImportContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useProductBulkImportContext requires ProductBulkImportProvider');
  return v;
}
